import { useEffect as useEffectPmc, useState as useStatePmc } from "react";
import { Api } from "./api";
import { Banner, Icon, isCapacitorNative } from "./components";

// PMC portfolio dashboard for company_admin users. Renders the
// company's HOA roster, aggregate unit_count, projected monthly bill,
// current subscription state, and a "Subscribe portfolio" action that
// kicks the Stripe Checkout flow for the PMC line.
//
// Per pricing.md v10 PMC is billed via a two-line-item subscription
// (tiered per-unit + $99 platform fee). The backend in
// app/billing/service.py and app/pmc/routes.py owns the math; this
// component just renders it.

function _fmtUsdP(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

function _fmtDateP(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

function PmcPortfolio({ onRoute }) {
  const [state, setState] = useStatePmc({ loading: true, error: null, data: null });
  const [pending, setPending] = useStatePmc(null);  // "checkout"
  const [actionError, setActionError] = useStatePmc(null);
  const [bannerStatus, setBannerStatus] = useStatePmc(null);

  const load = () => {
    setState({ loading: true, error: null, data: null });
    window.Api.pmcPortfolio()
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((err) => setState({
        loading: false,
        error: err.message || "Couldn't load portfolio.",
        data: null,
      }));
  };

  useEffectPmc(() => {
    load();
    // Pick up the ?billing=success|cancel return from Stripe.
    const url = new URL(window.location.href);
    const billing = url.searchParams.get("billing");
    if (billing === "success" || billing === "cancel") {
      setBannerStatus(billing);
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const subscribe = async () => {
    setPending("checkout");
    setActionError(null);
    // Stripe redirects back to a full page-load, which boots managers
    // to DEFAULT_ROUTE[manager] = "dashboard". Append a `?route=portfolio`
    // hint so the bootstrap in app.jsx restores the Portfolio view
    // (and the success/cancel banner here is what the user sees).
    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("route", "portfolio");
    url.searchParams.set("billing", "success");
    const success_url = url.toString();
    url.searchParams.set("billing", "cancel");
    const cancel_url = url.toString();
    try {
      const { url: checkoutUrl } = await window.Api.pmcCheckout({
        success_url, cancel_url,
      });
      window.location.href = checkoutUrl;
    } catch (err) {
      setActionError(err.message || "Couldn't start checkout.");
      setPending(null);
    }
  };

  if (state.loading) {
    return (
      <div className="scroll">
        <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
          Loading portfolio…
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="scroll">
        <div className="card card--pad-lg" style={{ color: "var(--danger)" }}>{state.error}</div>
      </div>
    );
  }

  const p = state.data;
  const totalUnits = p.total_units;
  const monthlyCents = p.projected_monthly_cents;
  const hasMissingUnitCount = p.communities.some(
    (c) => c.is_active && (c.unit_count == null || c.unit_count <= 0),
  );
  const activeCount = p.communities.filter((c) => c.is_active).length;
  const isSubscribed = p.subscription_status === "active" || p.subscription_status === "trialing";

  return (
    <div className="scroll">
      <div className="stack-lg" style={{ maxWidth: 960 }}>

        {bannerStatus === "success" && (
          <Banner icon="check" tone="accent">
            <b>Thanks!</b> Your portfolio subscription is being activated. It may take a few seconds for the new state to appear here.
          </Banner>
        )}
        {bannerStatus === "cancel" && (
          <Banner icon="info">
            Checkout cancelled — nothing was charged.
          </Banner>
        )}

        {/* Header */}
        <div className="card card--feature card--pad-lg">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                <span className="pill pill--ghost text-mono">{p.slug}</span>
                {p.subscription_status && (
                  <span className="pill pill--ghost">{p.subscription_status}</span>
                )}
              </div>
              <h2 className="display" style={{ fontSize: 32, fontWeight: 400, letterSpacing: "-0.02em" }}>
                {p.name}
              </h2>
              <p className="text-mute" style={{ marginTop: 6 }}>
                {activeCount} active {activeCount === 1 ? "community" : "communities"} ·{" "}
                {totalUnits.toLocaleString()} {totalUnits === 1 ? "household" : "households"}
              </p>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn--ghost btn--sm" onClick={load}>
                <Icon name="history" size={14} /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Billing summary */}
        <div className="card card--pad-lg">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
            <div>
              <div className="text-mute text-sm">Projected monthly bill</div>
              <div className="display" style={{ fontSize: 32, fontWeight: 400, marginTop: 4 }}>
                {_fmtUsdP(monthlyCents)} <span className="text-mute" style={{ fontSize: 14, fontWeight: 400 }}>/ month</span>
              </div>
              <div className="text-sm text-mute" style={{ marginTop: 4 }}>
                Tiered per-household + {_fmtUsdP(p.platform_fee_cents)} platform fee.{" "}
                {p.subscription_status === "active" && p.current_period_end && (
                  <>Renews {_fmtDateP(p.current_period_end)}.</>
                )}
                {!isSubscribed && (
                  <>This is what your subscription will cost — no charge yet.</>
                )}
              </div>
            </div>
            {!isSubscribed && !isCapacitorNative() && (
              <button
                className="btn btn--accent btn--lg"
                onClick={subscribe}
                disabled={pending === "checkout" || totalUnits <= 0}
                title={totalUnits <= 0 ? "Set household count on at least one community first" : undefined}
              >
                {pending === "checkout" ? "Opening checkout…" : "Subscribe portfolio"}
              </button>
            )}
            {isSubscribed && (
              <span className="pill pill--gold">Active</span>
            )}
          </div>
          {actionError && (
            <div role="alert" style={{
              marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 14,
              background: "var(--danger-soft, #fdecea)",
              color: "var(--danger, #b3261e)",
              border: "1px solid var(--danger, #b3261e)",
            }}>{actionError}</div>
          )}
          {hasMissingUnitCount && (
            <div style={{
              marginTop: 12, padding: 10, borderRadius: 8,
              background: "var(--bg-2)", fontSize: 13, lineHeight: 1.5,
            }}>
              <b>Heads up:</b> some communities don't have their household
              count set, so they're counted as zero in the total above. Open
              each one's manager dashboard and set the household count before
              subscribing.
            </div>
          )}
        </div>

        {/* Community roster */}
        <div className="card card--pad-lg">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Communities in this portfolio</div>
            <div className="text-sm text-mute">{p.communities.length}</div>
          </div>
          <div className="col" style={{ gap: 4 }}>
            {p.communities.length === 0 && (
              <div className="text-mute text-sm" style={{ padding: "12px 0" }}>
                No communities linked yet. Ask support to attach an HOA to your portfolio.
              </div>
            )}
            {p.communities.map((c, i) => (
              <div key={c.id} className="row" style={{
                justifyContent: "space-between",
                padding: "12px 0",
                borderBottom: i < p.communities.length - 1 ? "1px dashed var(--hairline)" : 0,
                gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{c.name}</div>
                  <div className="text-xs text-mute" style={{ marginTop: 2 }}>
                    <span className="text-mono">{c.slug}</span>
                    {!c.is_active && " · inactive"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {c.unit_count == null ? (
                    <span className="text-sm" style={{ color: "var(--danger)" }}>households not set</span>
                  ) : (
                    <span className="text-sm">
                      <b>{c.unit_count.toLocaleString()}</b> <span className="text-mute">{c.unit_count === 1 ? "household" : "households"}</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-mute" style={{ textAlign: "center" }}>
          Need to add or remove a community from this portfolio? Contact support.
        </div>
      </div>
    </div>
  );
}

export { PmcPortfolio };
